import { t } from 'i18next';

import { base64Decode, base64Encode, isBase64 } from '../utils/base64';
import { Logger } from '../utils/logger';
import { Answer } from './Answer';
import { AssignmentAttempt, ChoiceOrder } from './AssignmentAttempt';
import { AssignmentSource } from './AssignmentSource';
import { Book } from './Book';
import { BookOutline, flattenChapters } from './BookOutline';
import { getChapterById, getChapterId } from './Chapter';
import { Company } from './Company';
import { Content } from './Content';
import { ContentOutline } from './ContentOutline';
import { Edition } from './Edition';
import { FileProfile } from './FileProfile';
import { FlagCount } from './FlagCount';
import { Flashcard } from './Flashcard';
import { Item } from './Item';
import { deserializeNode } from './Node';
import { getPageById, getPageId } from './Page';
import { Product } from './Product';
import { ProductBookOutline } from './ProductBookOutline';
import { PromptType } from './PromptType';
import { QuestionAssignmentGrade } from './QuestionAssignmentGrade';
import { QuestionCourseSectionGrade } from './QuestionCourseSectionGrade';
import { QuestionDescriptor } from './QuestionDescriptor';
import { QuestionGrade } from './QuestionGrade';
import { Thread } from './Thread';
import { ThreadRepliesCount } from './ThreadRepliesCount';

export interface Choice {
  answer: string;
}

export enum QuestionFormat {
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  TRUE_FALSE = 'TRUE_FALSE',
  FLASHCARD_CHOICE = 'FLASHCARD_CHOICE',
  SELF_STUDY = 'SELF_STUDY',
  POLL = 'POLL',
  ATTENDANCE = 'ATTENDANCE',

  PROMPT = 'PROMPT',
}

export enum ChoiceType {
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  TRUE_FALSE = 'TRUE_FALSE',
}
export enum QuestionSource {
  PUBLISHER = 'PUBLISHER',
  INSTRUCTOR = 'INSTRUCTOR',
  STUDENT = 'STUDENT',
  AI = 'AI',
}

export enum UsageType {
  AlwaysUse = 'AlwaysUse',
  RandomizeUse = 'RandomizeUse',
}

export interface QuestionData extends Item {
  index: string;
  question: string;
  correctAnswer: string;
  incorrectAnswer1: string;
  incorrectAnswer2: string;
  incorrectAnswer3: string;
  chapterId: string;
  pageId: string;
  referenceLabel?: string;
}

export enum QuestionDifficulty {
  EASIEST = 'EASIEST',
  EASY = 'EASY',
  MEDIUM = 'MEDIUM',
  HARD = 'HARD',
  HARDEST = 'HARDEST',
}

export interface QuestionVideo {
  src: string;
  srt?: string;

  vtt?: string;
}

export interface Question extends Item {
  format: QuestionFormat;
  choiceType?: ChoiceType;

  // Adopted questions will have a reference to the original question
  parentQuestionId?: number;
  parentQuestion?: Question;

  // Adopted questions will have a reference to the product
  productId?: number;
  product?: Product;

  publisherId?: number;
  publisher?: Company;

  bookId?: number;
  book?: Book;

  editionId?: number;
  edition?: Edition;

  loId?: number;

  questionText: string;

  contentIds?: number[];
  contents?: Content[];

  choices: Choice[];

  answerIndex: number;

  points: number;

  explanation?: string;
  source?: QuestionSource;

  pageId?: number;
  chapterId?: number;

  flagCount?: FlagCount;

  document?: string;

  anchorId?: string;
  anchorOffset?: number;
  focusId?: string;
  focusOffset?: number;

  referenceLabel?: string;

  answers?: Answer[];

  files: FileProfile[];

  difficulty: QuestionDifficulty;

  contentId?: number;
  content?: Content;

  questionAssignmentGrade?: QuestionAssignmentGrade;
  questionCourseSectionGrade?: QuestionCourseSectionGrade;

  questionGrade?: QuestionGrade;
  relatedQuestions?: Question[];
  threads?: Thread[];
  replyCount?: ThreadRepliesCount;
  title?: string;

  promptType?: PromptType;

  image?: {
    src: string;
    alt?: string;
    width?: string;
    height?: string;
  };

  video?: QuestionVideo | null;
}

export const isQuestion = (item: Question | Flashcard): item is Question => {
  return (item as Question).questionText !== undefined;
};

const getYForNode = (document: Document, node: Node): number => {
  const range = document.createRange();
  range.selectNode(node);
  return range.getBoundingClientRect().y;
};

const isNullish = (object: any): boolean => {
  return object === null || object === undefined;
};

export const stackQuestions = (document: Document, questions: Question[]): Question[][] => {
  const yPositions: any = {};

  const chapterReferencedQuestions = questions.filter(
    (question: Question) => isNullish(question.pageId) && isNullish(question.anchorId)
  );

  for (const question of chapterReferencedQuestions) {
    let pos = 0;
    const node = document.getElementById(getChapterId(question.chapterId!));
    if (node) {
      pos = getYForNode(document, node);
    }

    if (!yPositions[pos]) {
      yPositions[pos] = [question];
    } else {
      yPositions[pos].push(question);
    }
  }

  const pageReferencedQuestions = questions.filter(
    (question: Question) => !isNullish(question.pageId) && isNullish(question.anchorId)
  );

  for (const question of pageReferencedQuestions) {
    const node = document.getElementById(getPageId(question.pageId!));
    let pos = 0;
    if (node) {
      pos = getYForNode(document, node);
    }

    if (!yPositions[pos]) {
      yPositions[pos] = [question];
    } else {
      yPositions[pos].push(question);
    }
  }

  const anchorReferencedQuestions = questions.filter(
    (question: Question) => !isNullish(question.anchorId)
  );

  for (const question of anchorReferencedQuestions) {
    const { node } = deserializeNode(document, question.anchorId!);
    let pos = 0;
    if (node) {
      pos = getYForNode(document, node);
    }

    if (!yPositions[pos]) {
      yPositions[pos] = [question];
    } else {
      yPositions[pos].push(question);
    }
  }

  const stack: Question[][] = [];
  const sortedKeys = Object.keys(yPositions).sort((a: any, b: any) => Number(a) - Number(b));
  for (const key of sortedKeys) {
    stack.push(yPositions[key]);
  }

  return stack;
};

export const translateQuestionFromTransport = (attributes: Question): Question => {
  const { questionText, explanation, choices, threads } = attributes;
  const translateChoices = [];
  for (const choice of choices ?? []) {
    let answer = isBase64(choice.answer) ? base64Decode(choice.answer) : choice.answer;
    // Repair data saved by the old isBase64 check: "True" (4 chars) was mistaken
    // for base64, decoded to "N��" and saved that way. "False" (5 chars) never
    // matched, so a garbled answer in a True/False question can only be "True".
    if (
      attributes.format === QuestionFormat.TRUE_FALSE &&
      typeof answer === 'string' &&
      answer.includes('\uFFFD')
    ) {
      answer = 'True';
    }
    translateChoices.push({
      ...choice,
      answer,
    });
  }

  const translateThreads: Thread[] = [];
  if (threads?.length) {
    for (const thread of threads) {
      translateThreads.push({
        ...thread,
        prompt: isBase64(thread.prompt) ? base64Decode(thread.prompt) : thread.prompt,
      });
    }
  }

  return {
    ...attributes,
    choices: translateChoices,
    threads: threads ? translateThreads : undefined,
    explanation: explanation && isBase64(explanation) ? base64Decode(explanation) : explanation,
    questionText: isBase64(questionText) ? base64Decode(questionText) : questionText,
  };
};

export const translateQuestionForTransport = (attributes: any) => {
  Logger.log(`translateQuestionForTransport:`, attributes);

  const { questionText, explanation, choices } = attributes;

  const translateChoices = [];
  for (const choice of choices ?? []) {
    translateChoices.push({
      ...choice,
      answer: !isBase64(choice.answer) ? base64Encode(choice.answer) : choice.answer,
    });
  }

  Logger.log(`translateQuestionForTransport:`, translateChoices);

  return {
    ...attributes,
    questionText: !isBase64(questionText) ? base64Encode(questionText) : questionText,
    explanation: !isBase64(explanation) ? base64Encode(explanation) : explanation,
    choices: translateChoices,
  };
};

export const translatePollQuestionFromTransport = (attributes: any) => {
  return {
    ...attributes,
    question: translateQuestionFromTransport(attributes.question),
  };
};

export const translatePollQuestionForTransport = (attributes: any) => {
  return {
    ...attributes,
    question: translateQuestionForTransport(attributes.question),
  };
};

export const translateBulkAssignmentFromTransport = (attributes: any) => {
  return {
    ...attributes,
    pollQuestion: attributes?.pollQuestion
      ? translatePollQuestionFromTransport(attributes.question)
      : undefined,
  };
};

export const translateBulkAssignmentForTransport = (attributes: any) => {
  return {
    ...attributes,
    pollQuestion: attributes?.pollQuestion
      ? translateQuestionForTransport(attributes.pollQuestion)
      : undefined,
  };
};

export const isCorrectAnswer = (question: Question, answer: Answer): boolean => {
  return question.answerIndex === answer.answerIndex;
};

export const orderChoices = (choices: Choice[], choiceOrder: ChoiceOrder[]) => {
  const orderedChoices: Choice[] = [];

  for (const choiceIndex of choiceOrder) {
    const choice = choices[choiceIndex.originalOrder];
    orderedChoices.push(choice);
  }

  return orderedChoices;
};

export const sortQuestionsByDoc = (questions: Question[]): Question[] => {
  const sortedQuestions: Question[] = questions.sort((a: Question, b: Question) => {
    if (a.pageId && b.pageId) {
      return a.pageId - b.pageId;
    }

    if (a.chapterId && b.chapterId) {
      return a.chapterId - b.chapterId;
    }

    return 1;
  });

  return sortedQuestions;
};

/** Outline shape shared by BookOutline, ProductBookOutline, and ContentOutline */
type AnyOutline = { chapters: import('./Chapter').Chapter[]; pages: import('./Page').Page[] };

/**
 * Sort questions using the reading order defined in the book outline.
 * - pageId: sorted by position of the page in bookOutline.pages array (reading order)
 * - chapterId: sorted by position in the flattened chapter tree (depth-first reading order)
 * - performance: sorted by questionCourseSectionGrade.percentCorrect
 * - confusionFactor: sorted by questionCourseSectionGrade.confusionFactor
 * Falls back to raw ID comparison when no outline is provided.
 */
export const sortQuestions = (
  questions: Question[],
  order: string,
  direction: string,
  outline?: AnyOutline | null
): Question[] => {
  const dir = direction === 'DESC' ? -1 : 1;
  const sorted = [...questions];

  if (order === 'pageId') {
    const pageIndexMap = new Map<number, number>();
    if (outline?.pages) {
      outline.pages.forEach((page, idx) => pageIndexMap.set(page.id, idx));
    }
    sorted.sort((a, b) => {
      const aIdx = a.pageId != null ? (pageIndexMap.get(a.pageId) ?? a.pageId) : Infinity;
      const bIdx = b.pageId != null ? (pageIndexMap.get(b.pageId) ?? b.pageId) : Infinity;
      return dir * (aIdx - bIdx);
    });
  } else if (order === 'chapterId') {
    const chapterIndexMap = new Map<number, number>();
    if (outline?.chapters) {
      const flat = flattenChapters(outline.chapters);
      flat.forEach((chapter, idx) => chapterIndexMap.set(chapter.id, idx));
    }
    sorted.sort((a, b) => {
      const aIdx =
        a.chapterId != null ? (chapterIndexMap.get(a.chapterId) ?? a.chapterId) : Infinity;
      const bIdx =
        b.chapterId != null ? (chapterIndexMap.get(b.chapterId) ?? b.chapterId) : Infinity;
      return dir * (aIdx - bIdx);
    });
  } else if (order === 'questioncoursesectiongrade.percentCorrect' || order === 'performance') {
    sorted.sort((a, b) => {
      const aVal = a.questionCourseSectionGrade?.percentCorrect ?? 0;
      const bVal = b.questionCourseSectionGrade?.percentCorrect ?? 0;
      return dir * (aVal - bVal);
    });
  } else if (
    order === 'questioncoursesectiongrade.confusionFactor' ||
    order === 'confusionFactor'
  ) {
    sorted.sort((a, b) => {
      const aVal = a.questionCourseSectionGrade?.confusionFactor ?? 0;
      const bVal = b.questionCourseSectionGrade?.confusionFactor ?? 0;
      return dir * (aVal - bVal);
    });
  }

  return sorted;
};

export const getSourceQuestions = (
  questions: Question[],
  source: AssignmentSource,
  attempt: AssignmentAttempt
): Question[] => {
  const { questionDescriptors } = attempt;
  const sourceQuestionDescriptors =
    questionDescriptors?.filter((descriptor: QuestionDescriptor) => {
      return Number(descriptor.sourceId) === source.id;
    }) ?? [];

  const sourceQuestions =
    questions
      ?.filter((question: Question) => {
        return sourceQuestionDescriptors.find((descriptor: QuestionDescriptor) => {
          return Number(descriptor.questionId) === question.id;
        });
      })
      ?.sort((a: Question, b: Question) =>
        a?.pageId && b?.pageId
          ? a?.pageId - b?.pageId
          : a?.chapterId && b?.chapterId
            ? a?.chapterId - b?.chapterId
            : -1
      ) ?? [];

  return sourceQuestions;
};

export const getChapterLabelForQuestion = (
  q: Question,
  bookOutline: BookOutline | ProductBookOutline | ContentOutline
): string => {
  if (!q || !bookOutline) {
    return '';
  }

  if (q.chapterId) {
    const chapter = getChapterById(bookOutline?.chapters ?? [], q.chapterId);
    return chapter ? chapter.label : '';
  }

  return '';
};

export const getPageLabelForQuestion = (
  q: Question,
  bookOutline: BookOutline | ProductBookOutline | ContentOutline
): string => {
  if (!q || !bookOutline) {
    return '';
  }

  if (q.pageId) {
    const page = getPageById(bookOutline?.pages, q.pageId);
    return page ? `${t('page')} ${page.label}` : '';
  }

  return '';
};
